import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getSharedPref, setSharedPref } from '../utils/sharedPrefs';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

// Preference is stored in a cookie scoped to `.condorsecurity.co` so it
// survives the landing↔app subdomain hop (each subdomain has its own
// localStorage). `getSharedPref` falls back to localStorage on dev/first-visit.
const STORAGE_KEY = 'condor_theme';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: ResolvedTheme;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);
}

/** Standalone resolved theme — works outside React (e.g. api.ts). */
// eslint-disable-next-line react-refresh/only-export-components
export function getResolvedTheme(): ResolvedTheme {
  const stored = getSharedPref(STORAGE_KEY) as Theme | null;
  const t = stored || 'dark';
  if (t === 'system') return getSystemTheme();
  return t as ResolvedTheme;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return { theme: 'dark' as Theme, setTheme: () => {}, resolvedTheme: 'dark' as ResolvedTheme };
  }
  return ctx;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = getSharedPref(STORAGE_KEY) as Theme | null;
    return stored || 'dark';
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    const stored = getSharedPref(STORAGE_KEY) as Theme | null;
    const t = stored || 'dark';
    return t === 'system' ? getSystemTheme() : t as ResolvedTheme;
  });

  const setTheme = useCallback((newTheme: Theme) => {
    setSharedPref(STORAGE_KEY, newTheme);
    setThemeState(newTheme);
    const resolved = newTheme === 'system' ? getSystemTheme() : newTheme as ResolvedTheme;
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  // Apply theme on mount
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for system theme changes when in 'system' mode
  useEffect(() => {
    if (theme !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      const resolved = e.matches ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  // Cross-tab sync: if the theme was changed in another tab (or in the
  // authenticated app shell while this tab shows the public landing),
  // mirror the change here. Fixes the case where a user toggled to
  // light post-login but the pre-login landing kept reading dark from
  // a stale React state after navigation.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      const next = e.newValue as Theme;
      setThemeState(next);
      const resolved = next === 'system' ? getSystemTheme() : (next as ResolvedTheme);
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
